<template>
  <div class="research-group-page">
    <div class="page-title">
      <img :src="logoRef">
      <div class="page-title-text-area">
        <h1 class="with-subhead">{{ dataGroupRef?.name }}</h1>
        <p class="subhead">
          {{ workspaceRef?.workspaceTypeInfo?.description }}
          managed by <UserLink :user-id="dataGroupRef?.ownerId" dimmed/>
        </p>
      </div>
    </div>
    <div>
      <ul ref="tabsElemRef" class="nav nav-pills research-group-tabs" role="tablist">
        <li class="nav-item">
          <button type="button" id="generalTab" class="nav-link active" data-bs-toggle="tab" data-bs-target="#generalPane" role="tab" aria-controls="generalPane" aria-selected="false">
            General
          </button>
        </li>
        <li class="nav-item">
          <button type="button" id="sharingTab" class="nav-link" data-bs-toggle="tab" data-bs-target="#sharingPane" role="tab" aria-controls="sharingPane" aria-selected="true">
            Sharing
          </button>
        </li>
        <li class="nav-item">
          <button type="button" id="manageTab" class="nav-link" data-bs-toggle="tab" data-bs-target="#managePane" role="tab" aria-controls="managePane" aria-selected="true">
            Manage
          </button>
        </li>
      </ul>
    </div>
    <div class="tab-content content-area">
      <section id="generalPane" class="tab-pane active" role="tabpanel" aria-labelledby="generalTab" tabindex="0">
        <LoadingIndicator v-if="!dataGroupRef"/>
        <template v-else>
          <div class="name">
            <label class="form-label">
              Name
            </label>
            <InPlaceTextEdit v-model="editDataGroup.name" required :disabled="!canEditRef"/>
          </div>
          <div class="description">
            <label class="form-label">
              Description
            </label>
            <InPlaceTextEdit v-model="editDataGroup.description" placeholder="None" :disabled="!canEditRef"/>
          </div>
          <div>
            <div>
              <label class="form-label">
                People in this tree
              </label>
              <div class="big-number">{{ statsRef?.personCount.toLocaleString() }}</div>
            </div>
          </div>
          <div>
            <label class="form-label">
              Start person
            </label>
            <ProfileCard :person-id="dataGroupRef?.startItemId"/>
          </div>
          <div>
            <label class="form-label">
              Created
            </label>
            <div>
              {{ DateTimeUtil.toRelativePastDate(dataGroupRef?.createdDate) }}
            </div>
          </div>
          <div>
            <label class="form-label">
              Last synced
            </label>
            <div>
              {{ DateTimeUtil.toRelativePastDate(lastSyncRef?.createdDate) }}
            </div>
          </div>
        </template>
      </section>
      <section id="sharingPane" class="tab-pane" role="tabpanel" aria-labelledby="sharingTab" tabindex="0">
        <ResearchGroupSharing :data-group-id="dataGroupId"/>
      </section>
      <section id="managePane" class="tab-pane" role="tabpanel" aria-labelledby="manageTab" tabindex="0">
        <ResearchGroupManage :data-group-id="dataGroupId"/>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page-title img {
  padding-top: 8px;
}

.research-group-tabs {
  max-width: 800px;
}

.tab-content {
  max-width: 800px;
}

#generalPane.active {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.description, .name {
  input {
    max-width: 600px;
    display: block;
  }
}

.big-number {
  font-size: 2rem;
  font-weight: 300;
  line-height: 1.2;
}
</style>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { usePageTitle } from '@/util/AppUtil'
import { computedAsync} from '@vueuse/core'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { useSyncStore } from '@/rd/SyncStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ItemPermissions, DataGroup } from '@/gp/GroupAdminModel'
import { LoadMode } from '@/util/AsyncData'
import { DateTimeUtil } from '@/util/LuxonUtil'
import { useTabsTelemetry } from '@/util/TabTelemetry'
import { useInitialTabRouting } from '@/util/InitialTabRouting'
import { useInstantSave } from '@/util/InstantSave'
import { workspaceTypeInfos } from '@/rd/ResearchDataModel'
import ResearchGroupSharing from './ResearchGroupSharing.vue'
import ResearchGroupManage from './ResearchGroupManage.vue'
import UserLink from '@/manage/users/UserLink.vue'
import InPlaceTextEdit from '@/util/InPlaceTextEdit.vue'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import ProfileCard from '../ProfileCard.vue'

const props = defineProps({
  dataGroupId: String
})

const editDataGroup = reactive(new DataGroup())
const editProperties = [
  'name',
  'description',
] as (keyof DataGroup)[]

const tabsElemRef = ref<HTMLUListElement>()

const workspaceStore = useWorkspaceStore()
const dataGroupStore = useDataGroupStore()
const syncStore = useSyncStore()

const dataGroupRef = computed(() => dataGroupStore.getAsyncGroup(props.dataGroupId, LoadMode.EnsureLoaded)?.data)
const workspaceRef = computed(() => workspaceStore.getAsyncWorkspacesForGroups([props.dataGroupId], LoadMode.EnsureLoaded).at(0)?.data)
const logoRef = computed(() => workspaceTypeInfos.find(t => t.id == (workspaceRef.value?.dataFileBaseType ?? 'TestData'))?.icon)
const lastSyncRef = computed(() => syncStore.getAsyncSync(workspaceRef.value?.lastSyncId, LoadMode.EnsureLoaded)?.data)

const statsRef = computedAsync(
  async () => (await workspaceStore.getStatisticsAsync([workspaceRef.value?.id])).at(0))

const permissionsRef = computed(() => dataGroupStore.getAsyncPermissions(props.dataGroupId, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const canEditRef = computed(() => (permissionsRef.value & ItemPermissions.Modify) > 0)

usePageTitle("Tree", () => dataGroupRef.value?.name)
useInitialTabRouting('generalTab')
useTabsTelemetry(tabsElemRef)

useInstantSave(dataGroupRef, editDataGroup, editProperties, 
  changes => dataGroupStore.patchAsync(props.dataGroupId!, changes))

</script>
